/* eslint-disable @typescript-eslint/no-magic-numbers */
import { useCallback, useEffect, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import type { JSX } from 'react';
import { Box, Text, Button, Icon, Checkbox, IconButton } from '@wistia/ui';
import { styled } from 'styled-components';
import { trackStructEvent } from '~/utilities/snowplow';
import { downloadFile } from '~/utilities/downloadFile';
import type { EmbedOptions } from '~/playerEmbedOptions/types';
import type { DownloadableMediaFile } from '~/singlePageApp/components/DownloadModal';
import { Wrapper } from './StoryboardDownloadRow';
import { CheckboxWrapper } from './SharedStyledComponents';
import { DEFAULT_PLAY_BUTTON_WIDTH, DEFAULT_PLAY_BUTTON_HEIGHT, PlayButton } from './PlayButton';

const PREVIEW_HEIGHT = 120;
const MAX_BTN_SCALE = 3;

const ThumbnailPreview = styled.div<{ src: string; width: number; height: number }>`
  align-items: center;
  background-color: black;
  background-image: url(${({ src }) => src});
  background-repeat: no-repeat;
  background-position: center;
  background-size: auto 100%;
  border-radius: 16px;
  display: flex;
  justify-content: center;
  height: ${({ height }) => height}px;
  width: ${({ width }) => width}px;
`;

type Props = {
  thumbnail: DownloadableMediaFile;
  embedOptions: EmbedOptions;
};

export const ThumbnailDownloadRow = ({ thumbnail, embedOptions }: Props): JSX.Element => {
  const { downloadUrl, width, height, extension } = thumbnail;
  const [showPlayButton, setshowPlayButton] = useState(false);
  const togglePlayIcon = () => setshowPlayButton(!showPlayButton);
  const [playButtonCanvas, setPlayButtonCanvas] = useState<HTMLCanvasElement | null>(null);
  const [pngCanvas, setPngCanvas] = useState<HTMLCanvasElement | null>(null);
  const [wasCopied, setWasCopied] = useState(false);

  const aspectRatio = width / height;
  const isVerticalVideo = aspectRatio < 1;
  const previewWidth = Math.round(width / (height / PREVIEW_HEIGHT));

  const playButtonCanvasRef = useCallback((node: HTMLCanvasElement) => {
    setPlayButtonCanvas(node);
  }, []);

  const pngCanvasRef = useCallback((node: HTMLCanvasElement) => {
    setPngCanvas(node);
  }, []);

  // convert thumbnail to PNG since that is they only image format supported by ClipboardItem
  useEffect(() => {
    if (extension === 'png' || pngCanvas === null) {
      return;
    }

    const ctx = pngCanvas.getContext('2d');
    if (ctx === null) {
      return;
    }

    const thumbnailImg = new Image();
    thumbnailImg.src = downloadUrl;
    thumbnailImg.crossOrigin = 'anonymous';

    thumbnailImg.onload = () => {
      pngCanvas.width = thumbnailImg.width;
      pngCanvas.height = thumbnailImg.height;
      ctx.drawImage(thumbnailImg, 0, 0);
    };
  }, [extension, downloadUrl, pngCanvas]);

  useEffect(() => {
    if (playButtonCanvas === null) {
      return;
    }

    const ctx = playButtonCanvas.getContext('2d');
    if (ctx === null) {
      return;
    }

    const thumbnailImg = new Image();
    thumbnailImg.src = downloadUrl;
    thumbnailImg.crossOrigin = 'anonymous';

    thumbnailImg.onload = () => {
      playButtonCanvas.width = thumbnailImg.width;
      playButtonCanvas.height = thumbnailImg.height;
      ctx.drawImage(thumbnailImg, 0, 0);

      const scaleWidth = thumbnailImg.width / 640;
      const scaleHeight = thumbnailImg.height / 360;
      const minScale = Math.min(scaleWidth, scaleHeight);
      const buttonScale = !isVerticalVideo && minScale > MAX_BTN_SCALE ? MAX_BTN_SCALE : minScale;
      const svgWidth = DEFAULT_PLAY_BUTTON_WIDTH * buttonScale;
      const svgHeight = DEFAULT_PLAY_BUTTON_HEIGHT * buttonScale;

      const playButtonSvg = ReactDOMServer.renderToStaticMarkup(
        <PlayButton
          embedOptions={embedOptions}
          isVerticalVideo={isVerticalVideo}
          width={svgWidth}
          height={svgHeight}
          buttonScale={buttonScale}
        />,
      );

      const playButtonOverlayImg = new Image();
      const playButtonSvgBlob = new Blob([playButtonSvg], { type: 'image/svg+xml' });
      const playButtonSvgUrl = URL.createObjectURL(playButtonSvgBlob);

      playButtonOverlayImg.src = playButtonSvgUrl;
      playButtonOverlayImg.onload = () => {
        ctx.drawImage(
          playButtonOverlayImg,
          (playButtonCanvas.width - playButtonOverlayImg.width) / 2,
          (playButtonCanvas.height - playButtonOverlayImg.height) / 2,
        );
      };
    };
  }, [playButtonCanvas, downloadUrl, embedOptions, isVerticalVideo, previewWidth]);

  const downloadThumbnail = async () => {
    const response = await fetch(downloadUrl);
    const blob = await response.blob();
    downloadFile(blob, 'thumbnail.png', 'image/png');
  };

  const downloadThumbnailWithPlayButton = () => {
    if (playButtonCanvas === null) {
      return;
    }

    const link = document.createElement('a');
    link.download = 'thumbnail_with_play_button.png';
    link.href = playButtonCanvas.toDataURL('image/png');
    link.click();
  };

  const handleDownload = () => {
    if (showPlayButton) {
      downloadThumbnailWithPlayButton();
    } else {
      void downloadThumbnail();
    }

    trackStructEvent({
      category: 'button',
      action: 'click',
      label: 'publish-download-thumbnail',
      property: 'play-button',
      value: showPlayButton ? 'true' : 'false',
    });
  };

  const copyImage = async () => {
    let imageUrl = downloadUrl;

    if (showPlayButton) {
      imageUrl = playButtonCanvas?.toDataURL('image/png') ?? '';
    } else {
      imageUrl = extension === 'png' ? downloadUrl : (pngCanvas?.toDataURL('image/png') ?? '');
    }

    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const item = new ClipboardItem({ 'image/png': blob });
      await navigator.clipboard.write([item]);

      trackStructEvent({
        category: 'button',
        action: 'click',
        label: 'publish-copy-thumbnail',
      });

      setWasCopied(true);

      setTimeout(() => {
        setWasCopied(false);
      }, 1500);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy image to clipboard', error);
    }
  };

  return (
    <>
      <CheckboxWrapper margin="0.5rem 0 1rem">
        <Checkbox
          label="Include play button"
          onChange={togglePlayIcon}
          checked={showPlayButton}
        />
      </CheckboxWrapper>

      <Wrapper>
        <ThumbnailPreview
          src={downloadUrl}
          width={200}
          height={PREVIEW_HEIGHT}
          role="img"
          aria-label="thumbnail"
        >
          {showPlayButton && (
            <PlayButton
              embedOptions={embedOptions}
              isVerticalVideo={isVerticalVideo}
              isPreview={true}
            />
          )}
        </ThumbnailPreview>

        <canvas
          ref={playButtonCanvasRef}
          style={{ display: 'none' }}
        />

        <canvas
          ref={pngCanvasRef}
          style={{ display: 'none' }}
        />

        <Box
          direction="column"
          gap="space-03"
        >
          <Text variant="body2">
            <strong>Thumbnail</strong>
          </Text>

          <Text
            variant="body3"
            prominence="secondary"
          >
            {extension?.toUpperCase() ?? 'PNG'} · {width} x {height}
          </Text>

          <Box gap="space-02">
            <Button
              onClick={handleDownload}
              leftIcon={<Icon type="download" />}
            >
              Download
            </Button>

            <IconButton
              colorScheme={wasCopied ? 'success' : 'standard'}
              label=""
              variant="soft"
              onClick={() => {
                void copyImage();
              }}
            >
              <Icon type={wasCopied ? 'checkmark' : 'save-as-copy'} />
            </IconButton>
          </Box>
        </Box>
      </Wrapper>
    </>
  );
};
